import Table from '@/components/general/Table/Table';
import { useRouter, usePhrase } from '@/hooks/general';
import Status from '@/components/blocks/wallet/Transactions/general/Status/Status';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { Currency } from '@/components/general/Currency/Currency';
import { memo, useCallback } from 'react';

import { Box, Button, Stack } from '@mui/material';
import { TransactionDate } from '../../Transactions/general/TransactionDate/TransactionDate';
import { INonMemberTransaction } from '@/types';

interface TransactionTableProps {
    currencyCode: string;
    currencySymbol: string | undefined;
    currencySymbolPosition: string | undefined;
    transactions: INonMemberTransaction[];
    handleTransactionHistoryClicked: (token: string) => void;
    selectedToken?: string;
}

const NonMemberTransactionTable = ({
    transactions,
    currencyCode,
    currencySymbol,
    currencySymbolPosition,
    handleTransactionHistoryClicked,
    selectedToken,
}: TransactionTableProps) => {
    const { t } = usePhrase();

    const router = useRouter();

    const handleShowDetailsClicked = useCallback(
        (token: string) => router.push(`/compensation/${token}`),
        [router],
    );

    return (
        <Stack>
            <Table
                data-testid="table"
                paginationVisible={false}
                tableData={{
                    details: {
                        'aria-label': 'transactions-table',
                    },
                    headers: [
                        { label: t(WALLET_PHRASES.DATE), key: 'date' },
                        { label: t(WALLET_PHRASES.STUDY_NUMBER), key: 'studyNumber' },
                        { label: t(WALLET_PHRASES.REFERENCE_NUMBER), key: 'referenceNum' },
                        { label: t(WALLET_PHRASES.COMPENSATION), key: 'compensation' },
                        { label: t(WALLET_PHRASES.PAYMENT_STATUS), key: 'status' },
                        { label: '', key: 'transactionHistory' },
                    ],
                    rows: transactions.map((trans) => ({
                        key: `${trans.transactionId}`,
                        isHighlighted: selectedToken === trans.token,
                        values: [
                            {
                                label: (
                                    <Box>
                                        {trans.createDt ? (
                                            <TransactionDate date={trans.createDt} />
                                        ) : (
                                            ''
                                        )}
                                    </Box>
                                ),
                                key: 'date',
                            },
                            {
                                label: <Box>{trans.projectNumber}</Box>,
                                key: 'studyNumber',
                            },
                            {
                                label: <Box>{trans.transactionId}</Box>,
                                key: 'referenceNum',
                            },
                            {
                                label: (
                                    <Box>
                                        {trans.showAmount && trans.amount ? (
                                            <Currency
                                                code={currencyCode}
                                                amount={trans.amount}
                                                symbol={currencySymbol}
                                                position={currencySymbolPosition}
                                            />
                                        ) : (
                                            t(WALLET_PHRASES.TBC)
                                        )}
                                    </Box>
                                ),
                                key: 'compensation',
                            },
                            {
                                label: (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            justifyContent: ['flex-end', 'flex-end', 'flex-start'],
                                        }}
                                    >
                                        <Status
                                            statusColor={trans.statusColor}
                                            statusPhraseId={trans.statusPhraseId}
                                        />
                                    </Box>
                                ),
                                key: 'status',
                            },
                            {
                                label: (
                                    <Box
                                        sx={{
                                            cursor: 'pointer',
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        {selectedToken !== trans.token && (
                                            <Button
                                                variant="contained"
                                                sx={{ mr: 2 }}
                                                onClick={() =>
                                                    handleShowDetailsClicked(trans.token)
                                                }
                                            >
                                                {t(trans.paymentDetailsButtonPhraseId)}
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                handleTransactionHistoryClicked(trans.token);
                                            }}
                                        >
                                            {t(WALLET_PHRASES.TRANSACTION_HISTORY)}
                                        </Button>
                                    </Box>
                                ),
                                key: 'buttons',
                            },
                        ],
                    })),
                }}
            />
        </Stack>
    );
};

export default memo(NonMemberTransactionTable);
